import { useState, useEffect, useRef } from 'react';
import './App.css';
import HeroOrbital from '#/components/HeroOrbital';
import UsageSteps from '#/components/UsageSteps';
import { iconUrl } from '#/utils/assetUrl';
import { appendCampaignToUrl } from '#/utils/campaignContext';
import { track } from '#/utils/analytics';
import { DEEPLINK_BENEFIT_SITE_HOME } from '#/constants/deeplinks';

function CountUp({
  value,
  suffix = '',
  prefix = '',
}: {
  value: number;
  suffix?: string;
  prefix?: string;
}) {
  const [display, setDisplay] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prefersReduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    )?.matches;
    if (prefersReduced) {
      setDisplay(value);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          observer.unobserve(el);
          const duration = 1200;
          const start = performance.now();
          const step = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplay(Math.round(eased * value));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return (
    <span ref={ref}>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prefersReduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    )?.matches;
    if (prefersReduced) {
      el.classList.add('revealed');
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('revealed');
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

const CATEGORIES = [
  {
    icon: 'icon_money_bag_coins_up_blue.png',
    name: '생활안정',
    code: 'NB0301',
    count: 2000,
  },
  {
    icon: 'icon_object_architecture_house_won_blue.png',
    name: '주거·자립',
    code: 'NB0302',
    count: 500,
  },
  {
    icon: 'icon_object_stationery_book.png',
    name: '보육·교육',
    code: 'NB0303',
    count: 1400,
  },
  {
    icon: 'icon_object_bag_briefcase.png',
    name: '고용·창업',
    code: 'NB0304',
    count: 800,
  },
  {
    icon: 'icon_etc_health_stethoscope_blue.png',
    name: '보건·의료',
    code: 'NB0305',
    count: 1200,
  },
  {
    icon: 'icon_object_shield_check_blue.png',
    name: '행정·안전',
    code: 'NB0306',
    count: 600,
  },
  {
    icon: 'icon_creature_humanbust_mombaby_yellow.png',
    name: '임신·출산',
    code: 'NB0307',
    count: 900,
  },
  {
    icon: 'icon_creature_hand_hold_heart_blue.png',
    name: '보호·돌봄',
    code: 'NB0308',
    count: 600,
  },
  {
    icon: 'icon_object_palette.png',
    name: '문화·환경',
    code: 'NB0309',
    count: 600,
  },
  {
    icon: 'icon_creature_humanbust_man_farmer.png',
    name: '농림축산어업',
    code: 'NB0310',
    count: 1700,
  },
];

const FEATURES = [
  {
    num: 1,
    icon: 'feat-magnifier.png',
    title: '내게 딱 맞는 혜택만 콕 집어서',
    desc: '나이, 지역, 직업 등 내 정보를 바탕으로 받을 수 있는 혜택을 자동으로 찾아드려요.',
  },
  {
    num: 2,
    icon: 'feat-phone-click.png',
    title: '삼쩜삼 앱 하나로 끝',
    desc: '여러 사이트를 돌아다닐 필요 없이 정부 혜택 정보를 삼쩜삼 앱에서 한번에 확인하세요.',
  },
  {
    num: 3,
    icon: 'feat-bell-alarm.png',
    title: '새로운 혜택, 놓치지 않게 알림',
    desc: '해당하는 혜택이 생기면 푸시 알림으로 바로 알려드려요. 신청 기간을 놓칠 걱정 없어요.',
  },
  {
    num: 4,
    icon: 'feat-hand-check.png',
    title: '혜택 확인부터 신청까지 한 번에',
    desc: '혜택 확인과 신청 절차를 삼쩜삼 앱 안에서 모두 처리할 수 있어요.',
  },
];

const FAQS = [
  {
    q: '혜택알리미는 어떤 서비스인가요?',
    a: '혜택알리미는 행정정보를 바탕으로 나의 생활과 자격 정보를 확인해 받을 수 있는 정부 지원과 혜택을 알려주는 맞춤형 알림서비스예요. 한 번만 가입하면 일일이 찾아보지 않아도 내게 맞는 혜택을 알림으로 받아볼 수 있어요.',
  },
  {
    q: '혜택알리미는 어떤 방식으로 맞춤 혜택을 찾아 안내하나요?',
    a: '이용자의 생활 상황(출산, 이사, 취업, 실직 등)과 자격 정보(연령, 소득, 가족 구성, 장애 여부 등)를 종합 분석해 받을 수 있는 정부 지원과 공공 혜택을 먼저 찾아 알려드려요. 복잡한 검색 없이 나와 가족에게 필요한 혜택과 정보를 한곳에서 확인할 수 있어요.',
  },
  {
    q: '혜택알리미는 어떤 혜택들을 안내하나요?',
    a: '현금 지원, 물품 제공, 이용권, 요금·세금 감면 등 생활에 직접 도움이 되는 다양한 지원부터 교육, 돌봄, 복지, 주거, 일자리 같은 생활밀착형 제도 안내까지 폭넓게 제공해요.',
  },
  {
    q: '혜택알리미에서 안내하는 혜택 목록은 계속 동일한가요?',
    a: '아니요. 안내되는 혜택은 이용자의 상황과 자격 정보를 기준으로 분석된 결과예요. 출산, 주소 이전, 소득 변동 등 생활에 변화가 생기면 혜택 목록도 자동으로 업데이트돼요.',
  },
];

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'faq-item--open' : ''}`}>
      <button className="faq-question" onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <img
          className="faq-chevron"
          src="https://cdn.3o3.co.kr/mystique/icons/outline_chevron_down.svg"
          alt=""
          width={20}
          height={20}
        />
      </button>
      {open && (
        <div className="faq-answer">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
}

function App() {
  const heroRef = useReveal();
  const categoriesRef = useReveal();
  const featuresRef = useReveal();
  const faqRef = useReveal();
  const ctaRef = useReveal();
  const pvSentRef = useRef(false);

  useEffect(() => {
    if (pvSentRef.current) return;
    pvSentRef.current = true;
    track('service_entry_pv', {
      event_key: 'gov_benefit_home_pv',
      url: window.location.href,
    });
  }, []);

  const deeplinkHref = appendCampaignToUrl(DEEPLINK_BENEFIT_SITE_HOME);

  /**
   * Every home CTA opens the same app deeplink, so the only thing that tells
   * them apart in Amplitude is `event_key` — one per placement.
   */
  const ctaProps = (eventKey: string) => ({
    href: deeplinkHref,
    onClick: () => track('service_conversion_sub', { event_key: eventKey }),
  });

  return (
    <div className="page-wrapper">
      {/* ===== Nav ===== */}
      <nav className="nav">
        <div className="nav-inner">
          <img
            src={iconUrl('szs-logo.png')}
            alt="삼쩜삼"
            className="nav-logo-img"
          />
          <a
            {...ctaProps('gov_benefit_home_nav_cta_click')}
            className="nav-cta"
          >
            앱에서 확인하기
          </a>
        </div>
      </nav>

      {/* ===== Hero ===== */}
      <section className="hero">
        <div ref={heroRef} className="hero-inner reveal-group">
          <div
            className="hero-logos reveal-child"
            style={{ '--reveal-i': 0 } as React.CSSProperties}
          >
            <img
              src={iconUrl('logo-digital-service.png')}
              alt="디지털서비스개방"
              className="hero-logo hero-logo--digital"
            />
            <img
              src={iconUrl('logo-mois.png')}
              alt="행정안전부"
              className="hero-logo hero-logo--mois"
            />
          </div>
          <div className="hero-content">
            <h1
              className="hero-title reveal-child"
              style={{ '--reveal-i': 1 } as React.CSSProperties}
            >
              삼쩜삼에서 약{' '}
              <span className="hero-title--blue">
                <CountUp value={10000} suffix="개의" />
              </span>
              <br />
              정부 혜택, 내게 맞는 것만 콕!
            </h1>
            <p
              className="hero-desc reveal-child"
              style={{ '--reveal-i': 2 } as React.CSSProperties}
            >
              흩어져 있어 놓치지 쉬운 정부 혜택 및 지원금 정보, 이제 삼쩜삼에서
              한 번에 찾으세요.
              <br />
              나의 상황을 바탕으로 개인별 맞춤 혜택을 찾아 알려드려요.
            </p>
            <a
              {...ctaProps('gov_benefit_home_hero_cta_click')}
              className="hero-cta reveal-child"
              style={{ '--reveal-i': 3 } as React.CSSProperties}
            >
              무료로 삼쩜삼 앱에서 확인하기
            </a>
          </div>
        </div>
        <HeroOrbital />
      </section>

      <UsageSteps afterOrbital />

      {/* ===== Categories ===== */}
      <section className="categories">
        <div ref={categoriesRef} className="categories-inner reveal-group">
          <h2 className="categories-title reveal-child">
            어떤 정부 혜택을 찾아볼 수 있나요?
          </h2>
          <div className="categories-gap-12" />
          <p className="categories-subtitle reveal-child">
            10가지 분야의 정부 혜택을 한 눈에 확인하세요.
          </p>
          <div className="categories-gap-36" />
          <div className="category-grid">
            {CATEGORIES.map((cat, i) => (
              <div
                key={cat.code}
                className="category-card reveal-child"
                style={{ '--reveal-i': i } as React.CSSProperties}
              >
                <img
                  className="category-icon"
                  src={iconUrl(cat.icon)}
                  alt={cat.name}
                />
                <div className="category-text">
                  <span className="category-name">{cat.name}</span>
                  <span className="category-count">
                    <CountUp value={cat.count} prefix="+" suffix="개" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Features ===== */}
      <section className="features">
        <div ref={featuresRef} className="features-inner reveal-group">
          <div className="features-header reveal-child">
            <h2 className="features-title">
              삼쩜삼 혜택알리미가 <br />
              특별한 <span className="features-title--blue">4가지</span> 이유
            </h2>
            <p className="features-subtitle">
              다른 곳에서는 찾기 어려운 혜택까지, <br />
              삼쩜삼에서 한 번에
            </p>
          </div>
          <div className="feature-grid">
            {FEATURES.map((f, i) => (
              <div
                key={f.num}
                className="feature-card reveal-child"
                style={{ '--reveal-i': i + 1 } as React.CSSProperties}
              >
                <div className="feature-card-content">
                  <span className="feature-num">{f.num}</span>
                  <div className="feature-text">
                    <h3 className="feature-text-title">{f.title}</h3>
                    <p className="feature-text-desc">{f.desc}</p>
                  </div>
                </div>
                <img src={iconUrl(f.icon)} alt="" className="feature-illust" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section">
        <div ref={faqRef} className="section-inner reveal-group">
          <h2 className="section-title reveal-child">자주 묻는 질문</h2>
          <div className="faq-list">
            {FAQS.map((faq, i) => (
              <div
                key={i}
                className="reveal-child"
                style={{ '--reveal-i': i + 1 } as React.CSSProperties}
              >
                <FaqItem question={faq.q} answer={faq.a} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="cta-banner">
        <div ref={ctaRef} className="cta-banner-box reveal-group">
          <img
            src={iconUrl('card-background-image.png')}
            alt=""
            className="cta-banner-bg"
          />
          <div className="cta-banner-content">
            <h2 className="reveal-child">
              내가 받을 수 있는 정부 혜택,
              <br />
              지금 확인해 보세요
            </h2>
            <a
              {...ctaProps('gov_benefit_home_bottom_cta_click')}
              className="cta-button reveal-child"
              style={{ '--reveal-i': 1 } as React.CSSProperties}
            >
              삼쩜삼 앱에서 확인하기
            </a>
          </div>
        </div>
        <HeroOrbital />
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-inner">
          <strong className="footer-company-title">(주)자비스앤빌런즈</strong>
          <div className="footer-links">
            <a href="https://team.3o3.co.kr">회사소개</a>
            <a href="https://app.3o3.co.kr/privacy">
              <b>개인정보 처리방침</b>
            </a>
          </div>
          <dl className="footer-info">
            <dt>대표자</dt>
            <dd>김범섭&nbsp;|&nbsp;</dd>
            <dt>개인정보보호책임자</dt>
            <dd>
              김범섭
              <br />
            </dd>
            <dt>사업자등록번호</dt>
            <dd>
              158-86-00171
              <br />
            </dd>
            <dt>통신판매업신고번호</dt>
            <dd>
              2017-서울강남-02346
              <br />
            </dd>
            <dt>사업장소재지</dt>
            <dd>
              <address>
                서울특별시 강남구 테헤란로 44길 8, 9층, 10층, 15층
              </address>
              <br />
            </dd>
            <dt>삼쩜삼 고객센터</dt>
            <dd>
              <a href="tel:16887198" rel="noopener noreferrer" target="_blank">
                1688-7198
              </a>
              ,{' '}
              <a
                href="https://pf.kakao.com/_xmmjqb/chat"
                rel="noopener noreferrer"
                target="_blank"
              >
                실시간 채팅상담(클릭)
              </a>{' '}
              (※ 사업장 방문 또는 대면 상담은 불가합니다.)
            </dd>
          </dl>
          <small className="footer-copyright">
            Copyright &copy; Jobis&amp;Villains Corp. All rights reserved.
          </small>
        </div>
      </footer>
    </div>
  );
}

export default App;
